/**
 * Delivery Settings Component - Admin Dashboard
 * Manages delivery-eligible pincodes and distance-based fees for Jaipur
 */

import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Search,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  IndianRupee,
  Navigation,
  Edit2,
  Save,
  X,
  Map as MapIcon,
  Clock,
} from 'lucide-react';
import deliveryService from '../../services/deliveryService';
import { fuzzyMatch } from '../../lib/fuzzySearch';
import ZoneMapEditor from './ZoneMapEditor';

const DeliverySettings = () => {
  const [activeOrderType, setActiveOrderType] = useState('on_demand');
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newPincode, setNewPincode] = useState('');
  const [newArea, setNewArea] = useState('');
  const [newDistance, setNewDistance] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDistance, setEditDistance] = useState('');
  const [editFee, setEditFee] = useState('');
  const [editThreshold, setEditThreshold] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [zoneEditorPincode, setZoneEditorPincode] = useState(null);
  const [opHours, setOpHours] = useState({ openTime: '06:00', closeTime: '22:30' });
  const [editingOpHours, setEditingOpHours] = useState(false);
  const [opHoursForm, setOpHoursForm] = useState({ openTime: '06:00', closeTime: '22:30' });
  const [savingOpHours, setSavingOpHours] = useState(false);

  useEffect(() => {
    fetchPincodes(activeOrderType);
    deliveryService.getOperationalHours().then(result => {
      if (result.success) {
        setOpHours(result.data);
        setOpHoursForm(result.data);
      }
    });
  }, [activeOrderType]);

  const fetchPincodes = async (orderType = activeOrderType) => {
    setLoading(true);
    const result = await deliveryService.getAllPincodes(orderType);
    if (result.success) {
      setPincodes(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError('');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSaveOpHours = async () => {
    setSavingOpHours(true);
    const result = await deliveryService.updateOperationalHours(opHoursForm.openTime, opHoursForm.closeTime);
    setSavingOpHours(false);
    if (result.success) {
      setOpHours(result.data);
      setEditingOpHours(false);
      showSuccess('Operational hours updated');
    } else {
      setError(result.error || 'Failed to update operational hours');
    }
  };

  const handleAdd = async () => {
    setError('');
    if (!newPincode.trim()) { setError('Please enter a pincode'); return; }
    if (!/^\d{6}$/.test(newPincode.trim())) { setError('Pincode must be exactly 6 digits'); return; }

    setAdding(true);
    const result = await deliveryService.addPincode(
      newPincode.trim(),
      activeOrderType,
      newArea.trim(),
      parseFloat(newDistance) || 0
    );
    if (result.success) {
      setPincodes(prev => [...prev, result.data].sort((a, b) => a.pincode.localeCompare(b.pincode)));
      setNewPincode('');
      setNewArea('');
      setNewDistance('');
      showSuccess(`Pincode ${result.data.pincode} added successfully`);
    } else {
      setError(result.error);
    }
    setAdding(false);
  };

  const handleToggle = async (id) => {
    setTogglingId(id);
    const result = await deliveryService.togglePincode(id);
    if (result.success) {
      setPincodes(prev => prev.map(p => p.id === id ? result.data : p));
      showSuccess(`Pincode ${result.data.isActive ? 'activated' : 'deactivated'}`);
    } else {
      setError(result.error);
    }
    setTogglingId(null);
  };

  const handleDelete = async (id, pincode) => {
    if (!window.confirm(`Permanently delete pincode ${pincode}? This cannot be undone.`)) return;
    setDeletingId(id);
    const result = await deliveryService.removePincode(id);
    if (result.success) {
      setPincodes(prev => prev.filter(p => p.id !== id));
      showSuccess(`Pincode ${pincode} removed`);
    } else {
      setError(result.error);
    }
    setDeletingId(null);
  };

  const startEditing = (p) => {
    setEditingId(p.id);
    setEditDistance(p.distanceKm?.toString() || '0');
    setEditFee(p.deliveryFee?.toString() || '0');
    setEditThreshold(p.deliveryThreshold?.toString() || '0');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditDistance('');
    setEditFee('');
    setEditThreshold('');
  };

  const handleSaveDistance = async (id) => {
    setSavingId(id);
    const result = await deliveryService.updateDistance(id, parseFloat(editDistance) || 0);
    if (result.success) {
      setPincodes(prev => prev.map(p => p.id === id ? result.data : p));
      showSuccess(`Distance updated, fee recalculated to ₹${result.data.deliveryFee}`);
      setEditingId(null);
    } else {
      setError(result.error);
    }
    setSavingId(null);
  };

  const handleSaveFee = async (id) => {
    setSavingId(id);
    const result = await deliveryService.updateFee(id, parseFloat(editFee) || 0);
    if (result.success) {
      setPincodes(prev => prev.map(p => p.id === id ? result.data : p));
      showSuccess(`Delivery fee updated to ₹${result.data.deliveryFee}`);
      setEditingId(null);
    } else {
      setError(result.error);
    }
    setSavingId(null);
  };

  const handleSaveThreshold = async (id) => {
    setSavingId(id);
    const result = await deliveryService.updateThreshold(id, parseFloat(editThreshold) || 0);
    if (result.success) {
      setPincodes(prev => prev.map(p => p.id === id ? result.data : p));
      showSuccess(`Delivery threshold updated to ₹${result.data.deliveryThreshold}`);
      setEditingId(null);
    } else {
      setError(result.error);
    }
    setSavingId(null);
  };

  const filtered = pincodes.filter(p =>
    p.pincode.includes(search) ||
    fuzzyMatch(search, p.area || '')
  );

  const activeCount = pincodes.filter(p => p.isActive).length;
  const avgFee = pincodes.length > 0 
    ? Math.round(pincodes.reduce((sum, p) => sum + (parseFloat(p.deliveryFee) || 0), 0) / pincodes.length)
    : 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Delivery Settings</h1>
        <p className="text-gray-600 text-sm sm:text-base">Manage delivery pincodes and distance-based fees for Jaipur</p>
      </div>

      {/* Operational Hours */}
      <div className="bg-white rounded-xl p-5 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-gray-800">On-Demand Operational Hours</h2>
          </div>
          {!editingOpHours ? (
            <button
              onClick={() => { setOpHoursForm(opHours); setEditingOpHours(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSaveOpHours}
                disabled={savingOpHours}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
              >
                {savingOpHours ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                onClick={() => setEditingOpHours(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          )}
        </div>
        {editingOpHours ? (
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Open Time</label>
              <input
                type="time"
                value={opHoursForm.openTime}
                onChange={e => setOpHoursForm(prev => ({ ...prev, openTime: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <span className="text-gray-400 mt-5">to</span>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Close Time</label>
              <input
                type="time"
                value={opHoursForm.closeTime}
                onChange={e => setOpHoursForm(prev => ({ ...prev, closeTime: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        ) : (
          <p className="text-gray-700 text-sm">
            Orders accepted between <span className="font-semibold text-green-700">{opHours.openTime}</span> and <span className="font-semibold text-green-700">{opHours.closeTime}</span>. Orders outside this window are queued for next-day delivery.
          </p>
        )}
      </div>

      {/* Order type tabs */}
      <div className="mb-6 inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
        {[
          { id: 'on_demand', label: 'On-Demand' },
          { id: 'scheduled', label: 'Scheduled' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              if (activeOrderType === tab.id) return;
              setActiveOrderType(tab.id);
              setEditingId(null);
              setSearch('');
            }}
            className={`px-5 sm:px-7 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeOrderType === tab.id
                ? 'bg-[#548B2E] text-white shadow'
                : 'text-gray-600 hover:text-[#2e4e18] hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xl sm:text-2xl font-bold text-gray-800">{pincodes.length}</p>
          <p className="text-xs sm:text-sm text-gray-500">Total Pincodes</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xl sm:text-2xl font-bold text-green-600">{activeCount}</p>
          <p className="text-xs sm:text-sm text-gray-500">Active</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xl sm:text-2xl font-bold text-red-500">{pincodes.length - activeCount}</p>
          <p className="text-xs sm:text-sm text-gray-500">Inactive</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xl sm:text-2xl font-bold text-blue-600">₹{avgFee}</p>
          <p className="text-xs sm:text-sm text-gray-500">Avg. Fee</p>
        </div>
      </div>

      {/* Fee Tiers Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <Navigation className="w-4 h-4" /> Distance-Based Fee Tiers
        </h3>
        <div className="flex flex-wrap gap-3 text-xs sm:text-sm text-blue-700">
          <span className="px-2 py-1 bg-blue-100 rounded">0-5 km: <strong>₹10</strong></span>
          <span className="px-2 py-1 bg-blue-100 rounded">5-10 km: <strong>₹20</strong></span>
          <span className="px-2 py-1 bg-blue-100 rounded">10-15 km: <strong>₹30</strong></span>
          <span className="px-2 py-1 bg-blue-100 rounded">15-20 km: <strong>₹30</strong></span>
          <span className="px-2 py-1 bg-blue-100 rounded">20+ km: <strong>₹30</strong></span>
        </div>
      </div>

      {/* Feedback Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Add Pincode */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 flex-wrap">
          <Plus className="w-5 h-5 text-[#548B2E]" />
          Add New Pincode
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            activeOrderType === 'on_demand' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {activeOrderType === 'on_demand' ? 'On-Demand list' : 'Scheduled list'}
          </span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pincode *</label>
            <input
              type="text"
              value={newPincode}
              onChange={(e) => setNewPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="302001"
              maxLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent font-mono text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Area Name</label>
            <input
              type="text"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              placeholder="Malviya Nagar"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Distance (km)</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={newDistance}
              onChange={(e) => setNewDistance(e.target.value)}
              placeholder="8.5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAdd}
              disabled={adding}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Pincode List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Search + Refresh */}
        <div className="p-4 border-b border-gray-100 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search pincode or area..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <button
            onClick={fetchPincodes}
            className="p-2 text-gray-500 hover:text-[#548B2E] hover:bg-gray-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#548B2E] mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Loading pincodes...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {pincodes.length === 0
                ? 'No pincodes added yet. Add your first Jaipur pincode above.'
                : 'No pincodes match your search.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-600">Pincode</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-600">Area</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600">Distance (km)</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600">Delivery Fee</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600">Free Delivery Threshold</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-semibold text-gray-800">{p.pincode}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {p.area || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {editingId === p.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={editDistance}
                            onChange={(e) => setEditDistance(e.target.value)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-[#548B2E]"
                          />
                          <button
                            onClick={() => handleSaveDistance(p.id)}
                            disabled={savingId === p.id}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Save distance"
                          >
                            {savingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-700 font-medium">{parseFloat(p.distanceKm || 0).toFixed(1)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {editingId === p.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-gray-500">₹</span>
                          <input
                            type="number"
                            min="0"
                            value={editFee}
                            onChange={(e) => setEditFee(e.target.value)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-[#548B2E]"
                          />
                          <button
                            onClick={() => handleSaveFee(p.id)}
                            disabled={savingId === p.id}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Override fee"
                          >
                            {savingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <span className={`font-semibold ${parseFloat(p.deliveryFee) === 0 ? 'text-green-600' : 'text-gray-700'}`}>
                          {parseFloat(p.deliveryFee) === 0 ? 'Free' : `₹${parseFloat(p.deliveryFee || 0)}`}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {editingId === p.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-gray-500">₹</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editThreshold}
                            onChange={(e) => setEditThreshold(e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-[#548B2E]"
                          />
                          <button
                            onClick={() => handleSaveThreshold(p.id)}
                            disabled={savingId === p.id}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Save delivery threshold"
                          >
                            {savingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <span className="font-semibold text-gray-700">
                          ₹{parseFloat(p.deliveryThreshold || 0).toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        p.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${p.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Manage zones */}
                        <button
                          onClick={() => setZoneEditorPincode(p)}
                          className="p-1.5 hover:bg-[#548B2E]/10 rounded-lg transition-colors"
                          title="Manage delivery zones"
                        >
                          <MapIcon className="w-4 h-4 text-[#548B2E]" />
                        </button>
                        {/* Edit distance/fee */}
                        {editingId === p.id ? (
                          <button
                            onClick={cancelEditing}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Cancel editing"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        ) : (
                          <button
                            onClick={() => startEditing(p)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit distance/fee"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                        )}
                        {/* Toggle active/inactive */}
                        <button
                          onClick={() => handleToggle(p.id)}
                          disabled={togglingId === p.id}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          title={p.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {togglingId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                          ) : p.isActive ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(p.id, p.pincode)}
                          disabled={deletingId === p.id}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete permanently"
                        >
                          {deletingId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <p className="text-sm text-amber-800">
          <strong>How it works:</strong> Enter the distance from your warehouse and the fee is calculated automatically based on tiers.
          You can also manually override the fee if needed. Customers will see the delivery fee at checkout based on their pincode.
        </p>
        <p className="text-sm text-amber-800 mt-2">
          <strong>Delivery zones:</strong> Click the map icon next to any pincode to draw polygonal sub-areas. When a pincode has zones,
          customers must place their pin inside an active polygon to qualify for delivery. Pincodes with no zones use the legacy pincode-only rule.
        </p>
        <p className="text-sm text-amber-800 mt-2">
          <strong>Two separate lists:</strong> On-Demand and Scheduled fulfillment are configured independently. The same pincode can be active
          in one list and inactive in the other, with different fees and polygons per list. Use the tabs at the top to switch between them.
        </p>
      </div>

      {zoneEditorPincode && (
        <ZoneMapEditor
          pincode={zoneEditorPincode}
          onClose={() => setZoneEditorPincode(null)}
        />
      )}
    </div>
  );
};

export default DeliverySettings;